
import { supabase } from '@/integrations/supabase/client';
import { Parameters, parseParameterValue, ParameterType, fetchParameters } from '@communitly/shared';

/**
 * Loads the default category group for a community from parameters
 * 
 * @param communityId The ID of the community
 * @param parameters Optional parameters object to avoid re-fetching
 * @returns The default category group ID or null
 */
export const loadDefaultCategoryGroup = async (
  communityId: string, 
  parameters?: Parameters | null
): Promise<string | null> => {
  if (parameters && parameters['default_category_group']) {
    return parameters['default_category_group'].value as string;
  }
  
  try {
    const { data, error } = await supabase
      .from('parameters')
      .select('*')
      .eq('community_id', communityId)
      .eq('key', 'default_category_group')
      .single();
      
    if (error) {
      console.error('Error fetching default category group:', error);
      return null;
    }
    
    if (data) {
      const value = parseParameterValue(data.value, data.type as ParameterType);
      return value as string;
    }
    
    return null;
  } catch (error) {
    console.error('Error in loadDefaultCategoryGroup:', error);
    return null;
  }
};

/**
 * Get parameter value safely with optional default value
 * @param parameters Parameters object
 * @param key Parameter key
 * @param defaultValue Optional default value
 * @returns Parameter value or default
 */
export const safeGetParameter = <T extends string | boolean | number>(
  parameters: Parameters | null, 
  key: string,
  defaultValue?: T
): T | null => {
  if (!parameters || !parameters[key]) {
    return defaultValue ?? null;
  }
  return parameters[key].value as T;
};

/**
 * Get the default locale for a community
 * @param parameters Optional parameters object
 * @returns The default locale or 'en_GB' as fallback
 */
export const getDefaultLocale = (
  parameters: Parameters | null
): string => {
  const defaultLocale = safeGetParameter(parameters, 'default_locale', 'en_GB');
  return defaultLocale || 'en_GB';
};

/**
 * Get the default business banner URL for a community
 * @param communityId The ID of the community
 * @returns The default business banner URL or null
 */
export const getDefaultBusinessBannerUrl = async (
  communityId: string
): Promise<string | null> => {
  try {
    const { data, error } = await supabase
      .from('communities')
      .select('default_business_banner_url')
      .eq('id', communityId)
      .single();
    
    if (error) {
      console.error('Error fetching default business banner URL:', error);
      return null;
    }
    
    return data?.default_business_banner_url || null;
  } catch (error) {
    console.error('Error in getDefaultBusinessBannerUrl:', error);
    return null;
  }
};

/**
 * Generate the path for community assets in the storage bucket
 * @param communityId The ID of the community
 * @param fileName The file name
 * @returns The full path for the asset
 */
export const getCommunityAssetPath = (
  communityId: string,
  fileName: string
): string => {
  return `community_assets/${communityId}/${fileName}`;
};
